import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Users, RefreshCw, AlertTriangle, Sparkles, TrendingUp, TrendingDown, Minus, ExternalLink, Info, Copy, Check, ArrowRight,
} from "lucide-react";
import * as api from "../lib/api";
import { PRIMARY, fmt, usePersistentState } from "../components/search-performance/shared";
import { BackfillCard } from "../components/search-performance/ClusterAuthority";
import { openSearchTopic } from "../components/search-performance/TopicParts";

// Schwartz's pyramid, top (most aware) → bottom (unaware).
const LEVELS = ["most_aware", "product_aware", "solution_aware", "problem_aware", "unaware"];
const LEVEL_COLORS = {
  most_aware:     "#F59E0B",
  product_aware:  "#6EE7B7",
  solution_aware: "#14B8A6",
  problem_aware:  "#3F3F46",
  unaware:        "#A3BFB0",
};

const ROLE_LABELS = { learner: "Learner", practitioner: "Practitioner", manager: "Manager", executive: "Executive", unknown: "Unknown" };
const ROLE_COLORS = { learner: "#D4D4D8", practitioner: "#A1A1AA", manager: "#0EA5E9", executive: "#018D4C", unknown: "#F4F4F5" };

const VERDICTS = {
  strengthen: { label: "Strengthen", bg: "#ECFDF5", text: "#065F46", hint: "Your buyers search this — push it up" },
  bridge:     { label: "Bridge",     bg: "#FFFBEB", text: "#B45309", hint: "On-topic but early-stage — add a next step" },
  prune:      { label: "Prune",      bg: "#FEF2F2", text: "#DC2626", hint: "Mostly not your buyer — stop investing" },
  keep:       { label: "Keep",       bg: "#EFF6FF", text: "#1D4ED8", hint: "Right audience, already top 3" },
  unknown:    { label: "Unknown",    bg: "#FAFAFA", text: "#A1A1AA", hint: "" },
};

const TREND = {
  improving:         { label: "Improving", text: "#065F46", Icon: TrendingUp },
  steady:            { label: "Steady",    text: "#52525B", Icon: Minus },
  drifting:          { label: "Drifting",  text: "#DC2626", Icon: TrendingDown },
  insufficient_data: { label: "Too little history", text: "#A1A1AA", Icon: Minus },
};

const levelLabel = (k, playbook) => playbook?.[k]?.label || (k === "unknown" ? "Unknown" : k);
const fit2 = (f) => (f == null ? "—" : f.toFixed(2));
const path = (url) => { try { return new URL(url).pathname; } catch { return url; } };
const signed = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}pp`);

function Card({ title, hint, right, children, className = "" }) {
  return (
    <div className={`bg-white border border-zinc-100 rounded-xl ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4">
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400">{title}</p>
          {hint && <p className="text-[11px] text-zinc-400 mt-0.5">{hint}</p>}
        </div>
        {right}
      </div>
      <div className="px-5 pb-4 pt-3">{children}</div>
    </div>
  );
}

function VerdictChip({ verdict }) {
  const v = VERDICTS[verdict] || VERDICTS.unknown;
  return (
    <span title={v.hint} className="inline-flex px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-full whitespace-nowrap"
      style={{ background: v.bg, color: v.text }}>
      {v.label}
    </span>
  );
}

function StackBar({ mix, colors, labels, height = 6 }) {
  const parts = (mix || []).filter(m => m.impression_share > 0);
  return (
    <div className="flex w-full rounded-full overflow-hidden bg-zinc-100" style={{ height }}>
      {parts.map(m => (
        <div key={m.key} title={`${labels(m.key)}: ${m.impression_share}% of impressions`}
          style={{ width: `${m.impression_share}%`, background: colors[m.key] || "#E4E4E7" }} />
      ))}
    </div>
  );
}

// ── Headline ───────────────────────────────────────────────────────────────

function Headline({ data }) {
  const s = data.summary;
  const t = TREND[data.trend.verdict] || TREND.insufficient_data;
  const role = ROLE_LABELS[s.dominant_role] || "—";
  const stage = levelLabel(s.dominant_awareness, data.playbook);
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-100 border border-zinc-100 rounded-xl overflow-hidden mb-4">
      <div className="bg-white px-5 py-4 md:col-span-2">
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400">Right audience?</p>
        <p className="mt-1 text-2xl font-bold text-zinc-900">
          {s.on_target_click_share == null ? "—" : `${s.on_target_click_share}%`}
          <span className="text-xs font-medium text-zinc-400 ml-2">of clicks come from your buyers</span>
        </p>
        <p className="text-[11px] text-zinc-500 mt-1">
          Typical searcher: <b className="text-zinc-800">{stage}</b> · <b className="text-zinc-800">{role}</b>
          {s.dominant_persona && <> · persona <b className="text-zinc-800">{s.dominant_persona}</b></>}
        </p>
      </div>
      <div className="bg-white px-5 py-4">
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400">Direction</p>
        <p className="mt-1 text-sm font-bold flex items-center gap-1.5" style={{ color: t.text }}><t.Icon size={15} /> {t.label}</p>
        <p className="text-[11px] text-zinc-400 mt-0.5">
          Buyer share {signed(data.trend.on_target_change_pp)}, managers+execs {signed(data.trend.decision_role_change_pp)} (last 4 wks vs 4 before)
        </p>
      </div>
      <div className="bg-white px-5 py-4">
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400">Decision makers</p>
        <p className="mt-1 text-sm font-bold text-zinc-900">{s.decision_role_share == null ? "—" : `${s.decision_role_share}%`}</p>
        <p className="text-[11px] text-zinc-400 mt-0.5">
          of impressions from managers / executives · based on {s.coverage_click_share ?? "—"}% of clicks ({fmt(s.queries)} classified queries)
        </p>
      </div>
    </div>
  );
}

// ── Awareness pyramid ──────────────────────────────────────────────────────

function Pyramid({ data, metric }) {
  const byLevel = Object.fromEntries(data.stages.map(s => [s.level, s]));
  const total = data.stages.reduce((n, s) => n + (s[metric] || 0), 0);
  return (
    <Card
      title="Awareness levels (Schwartz)"
      hint="Which stage your searchers are at, and whether you rank where your buyers are."
    >
      <div className="space-y-1.5">
        {LEVELS.map((lvl, i) => {
          const s = byLevel[lvl] || {};
          const share = total ? (100 * (s[metric] || 0)) / total : 0;
          const inset = (LEVELS.length - 1 - i) * 7; // pyramid: narrow at the top
          return (
            <div key={lvl} className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-3 items-center">
              <div className="relative h-10">
                <div className="absolute inset-y-0 flex items-center justify-center rounded-md text-[10px] font-bold uppercase tracking-wide"
                  style={{ left: `${inset}%`, right: `${inset}%`, background: LEVEL_COLORS[lvl],
                           color: ["problem_aware", "solution_aware"].includes(lvl) ? "#fff" : "#18181b" }}>
                  {s.label || lvl}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-[1.4fr_0.6fr_0.6fr_0.7fr_1.6fr] gap-x-4 gap-y-1 items-center text-[11px]">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${share}%`, background: LEVEL_COLORS[lvl] }} />
                    </div>
                    <span className="w-10 text-right font-bold text-zinc-800">{share.toFixed(0)}%</span>
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{fmt(s.queries)} queries · {fmt(s.impressions)} impr</p>
                </div>
                <div title="Impression-weighted buyer fit (0–1)">
                  <p className="text-[9px] uppercase text-zinc-400 font-bold">Fit</p>
                  <p className="font-bold" style={{ color: (s.fit ?? 0) >= 0.5 ? "#065F46" : "#71717a" }}>{fit2(s.fit)}</p>
                </div>
                <div title="Share of this level's impressions from queries ranking top 10">
                  <p className="text-[9px] uppercase text-zinc-400 font-bold">Top 10</p>
                  <p className="font-bold text-zinc-700">{s.top10_share == null ? "—" : `${s.top10_share}%`}</p>
                </div>
                <div title="Queries at this level with no ranking page">
                  <p className="text-[9px] uppercase text-zinc-400 font-bold">No page</p>
                  <p className="font-bold" style={{ color: s.missing_queries ? "#1D4ED8" : "#a1a1aa" }}>{fmt(s.missing_queries)}</p>
                </div>
                <div className="col-span-2 md:col-span-1 text-[10px] text-zinc-500 leading-snug">
                  <span className="text-zinc-400">Wants to</span> {s.reader_intent?.toLowerCase()} ·{" "}
                  <span className="text-zinc-400">say</span> “{s.key_message}” ·{" "}
                  <span className="text-zinc-400">with</span> {s.content?.toLowerCase()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Who: role + persona ────────────────────────────────────────────────────

function MixList({ mix, colors, labels, metric }) {
  const key = metric === "clicks" ? "click_share" : "impression_share";
  return (
    <div className="space-y-2">
      {mix.map(m => (
        <div key={m.key} className="flex items-center gap-2 text-[11px]">
          <span className="w-28 truncate text-zinc-700" title={labels(m.key)}>{labels(m.key)}</span>
          <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${m[key]}%`, background: colors(m.key) }} />
          </div>
          <span className="w-10 text-right font-bold text-zinc-800">{m[key]}%</span>
          <span className="w-14 text-right text-zinc-400">{fmt(m.clicks)} clk</span>
        </div>
      ))}
    </div>
  );
}

function Who({ data, metric }) {
  const personaLabel = k => (k === "not_our_buyer" ? "Not our buyer" : k === "unknown" ? "Unassigned" : k);
  const personaColor = k => (k === "not_our_buyer" ? "#FCA5A5" : k === "unknown" ? "#E4E4E7" : PRIMARY);
  const noPersonas = data.mixes.persona.every(m => ["not_our_buyer", "unknown"].includes(m.key));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <Card title="Seniority" hint={`Are you reaching juniors or the people who decide? Share of ${metric}.`}>
        <MixList mix={data.mixes.role_level} colors={k => ROLE_COLORS[k]} labels={k => ROLE_LABELS[k] || k} metric={metric} />
      </Card>
      <Card title="Buyer personas" hint="Persona assigned only when the searcher plausibly is your buyer.">
        {noPersonas && (
          <p className="text-[11px] text-zinc-500 mb-2">
            No persona matches yet. <Link to="/dashboard/settings/business" className="underline">Define buyer personas</Link> and re-run Gap Analysis.
          </p>
        )}
        <MixList mix={data.mixes.persona} colors={personaColor} labels={personaLabel} metric={metric} />
      </Card>
    </div>
  );
}

// ── Trend ──────────────────────────────────────────────────────────────────

const fmtWeek = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
const TREND_LINES = [
  { id: "on_target_share", name: "Buyer share", color: PRIMARY },
  { id: "decision_role_share", name: "Managers + execs", color: "#0EA5E9" },
  { id: "aw_late", name: "Solution-aware or later", color: "#F59E0B" },
];

function TrendChart({ trend }) {
  const data = trend.series.map(w => ({
    week: w.week,
    on_target_share: w.on_target_share,
    decision_role_share: w.decision_role_share,
    aw_late: w.awareness?.solution_aware == null ? null
      : +((w.awareness.solution_aware || 0) + (w.awareness.product_aware || 0) + (w.awareness.most_aware || 0)).toFixed(1),
  }));
  return (
    <Card title="Are we moving toward the right audience?" hint="Weekly share of impressions, history re-read with today's classification." className="mb-4">
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#F4F4F5" vertical={false} />
            <XAxis dataKey="week" tickFormatter={fmtWeek} tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} minTickGap={24} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} width={36} tickFormatter={v => `${v}%`} />
            <Tooltip
              labelFormatter={w => `Week of ${fmtWeek(w)}`}
              formatter={(v, id) => [v == null ? "—" : `${v}%`, TREND_LINES.find(l => l.id === id)?.name || id]}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E4E4E7" }}
            />
            {TREND_LINES.map(l => <Line key={l.id} dataKey={l.id} stroke={l.color} strokeWidth={2} dot={false} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
        {TREND_LINES.map(l => (
          <span key={l.id} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
            <span className="w-2.5 h-0.5 rounded" style={{ background: l.color }} /> {l.name}
          </span>
        ))}
      </div>
    </Card>
  );
}

// ── Problem segments ───────────────────────────────────────────────────────

// Link from an audience group to the same group in Search Performance → Search topics.
function TopicLink({ id }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => openSearchTopic(navigate, id)}
      className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold hover:underline" style={{ color: PRIMARY }}>
      Coverage &amp; next move in Search topics <ArrowRight size={10} />
    </button>
  );
}

function Segments({ segments, playbook, building, onBuild }) {
  const items = segments.items || [];
  // Topic-backed segments are rebuilt by Gap Analysis, so there's nothing to rebuild here.
  const fromTopics = segments.source === "topics";
  return (
    <Card
      title="Who has which problem"
      hint={fromTopics
        ? `Same groups as Search Performance → Search topics · rebuilt by each Gap Analysis${segments.built_at ? ` · last ${new Date(segments.built_at).toLocaleDateString()}` : ""}`
        : segments.built_at
          ? `Queries grouped by the problem behind them · built ${new Date(segments.built_at).toLocaleDateString()}`
          : "Group your classified queries into the problems people bring to your site."}
      right={fromTopics ? null : (
        <button onClick={onBuild} disabled={building}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-40"
          style={{ background: PRIMARY }}>
          <Sparkles size={11} className={building ? "animate-pulse" : ""} />
          {building ? "Grouping…" : items.length ? "Rebuild" : "Find problem segments"}
        </button>
      )}
      className="mb-4"
    >
      {!items.length ? (
        <p className="text-[11px] text-zinc-400">No segments yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map(s => (
            <div key={s.id} id={`seg-${s.id}`} className="border border-zinc-100 rounded-lg p-3.5 scroll-mt-6 target:ring-2 target:ring-emerald-200">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-zinc-900">{s.name}</p>
                  {s.problem && <p className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{s.problem}</p>}
                </div>
                <VerdictChip verdict={s.verdict} />
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-2 text-[10px] text-zinc-500">
                <span><b className="text-zinc-800">{fmt(s.impressions)}</b> impr</span>
                <span><b className="text-zinc-800">{fmt(s.clicks)}</b> clicks</span>
                <span>fit <b className="text-zinc-800">{fit2(s.fit)}</b></span>
                <span>{levelLabel(s.dominant_awareness, playbook)} · {ROLE_LABELS[s.dominant_role] || "—"}</span>
                {s.missing_queries > 0 && <span className="text-blue-700">{s.missing_queries} with no page</span>}
              </div>
              <div className="mt-2">
                <StackBar mix={s.awareness_mix} colors={LEVEL_COLORS} labels={k => levelLabel(k, playbook)} />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {s.top_queries.slice(0, 6).map(q => (
                  <span key={q} className="px-1.5 py-0.5 text-[10px] bg-zinc-50 text-zinc-600 rounded">{q}</span>
                ))}
              </div>
              {fromTopics && <TopicLink id={s.id} />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Unmet demand ───────────────────────────────────────────────────────────

function demandSummary(demand, playbook) {
  const blocks = demand.groups.map(g => [
    `${g.name} — ${fmt(g.impressions)} impressions, ${g.queries} queries`,
    g.problem && `  ${g.problem}`,
    `  Searcher: ${levelLabel(g.dominant_awareness, playbook)} · ${ROLE_LABELS[g.dominant_role] || "—"}${g.dominant_persona ? ` · ${g.dominant_persona}` : ""}`,
    ...g.top_queries.map(q => `  • "${q.query}" — ${fmt(q.impressions)} impr, ${q.position ? `ranks ${q.position}` : "no page"}`),
  ].filter(Boolean).join("\n"));
  return [
    `Unmet buyer demand — ${demand.queries} queries · ${fmt(demand.impressions)} impressions`,
    "Our buyers search these; no page on our site serves them (no page, beyond position 20, or 0 clicks).",
    "",
    blocks.join("\n\n"),
  ].join("\n");
}

function UnmetDemand({ demand, playbook, fromTopics }) {
  const [copied, setCopied] = useState(false);
  if (!demand?.groups?.length) return null;
  const copy = () => navigator.clipboard.writeText(demandSummary(demand, playbook)).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });
  return (
    <Card
      title="Unmet buyer demand"
      hint={`${demand.queries} buyer queries no page serves (${demand.no_page} with no page, rest beyond position 20 or 0 clicks) · ${fmt(demand.impressions)} impressions. A content gap, or a feature to build.`}
      right={
        <button onClick={copy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold rounded-lg border border-zinc-200 hover:bg-zinc-50">
          {copied ? <Check size={11} className="text-green-600" /> : <Copy size={11} />}
          {copied ? "Copied" : "Copy for product team"}
        </button>
      }
      className="mb-4"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {demand.groups.map(g => (
          <div key={g.key} className="border border-zinc-100 rounded-lg p-3.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-bold text-zinc-900">{g.name}</p>
                {g.problem && <p className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{g.problem}</p>}
              </div>
              <span className="text-[11px] font-bold text-zinc-800 whitespace-nowrap">{fmt(g.impressions)} impr</span>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-2 text-[10px] text-zinc-500">
              <span>{g.queries} queries{g.no_page > 0 && <span className="text-blue-700"> · {g.no_page} no page</span>}</span>
              <span>fit <b className="text-zinc-800">{fit2(g.fit)}</b></span>
              <span>{levelLabel(g.dominant_awareness, playbook)} · {ROLE_LABELS[g.dominant_role] || "—"}</span>
              {g.dominant_persona && <span>{g.dominant_persona}</span>}
            </div>
            {g.build && <p className="text-[11px] text-zinc-700 mt-2"><span className="text-zinc-400">Build →</span> {g.build}</p>}
            <div className="flex flex-wrap gap-1 mt-2">
              {g.top_queries.map(q => (
                <span key={q.query} title={q.url ? `Ranks ${q.position} with ${path(q.url)}` : "No ranking page"}
                  className="px-1.5 py-0.5 text-[10px] bg-zinc-50 text-zinc-600 rounded">
                  {q.query} <span className="text-zinc-400">{fmt(q.impressions)} · {q.position ? `#${Math.round(q.position)}` : "no page"}</span>
                </span>
              ))}
            </div>
            {fromTopics && !String(g.key).startsWith("aw:") && <TopicLink id={g.key} />}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Page verdicts ──────────────────────────────────────────────────────────

function Pages({ pages, playbook }) {
  const [filter, setFilter] = usePersistentState("audience.pageFilter", "strengthen");
  const counts = pages.reduce((acc, p) => ({ ...acc, [p.verdict]: (acc[p.verdict] || 0) + 1 }), {});
  const shown = pages.filter(p => p.verdict === filter);
  return (
    <Card
      title="Prune · Strengthen · Bridge"
      hint="Per ranking page, from the audience of the queries it ranks for."
      right={
        <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
          {["strengthen", "bridge", "prune", "keep"].map(k => (
            <button key={k} onClick={() => setFilter(k)} title={VERDICTS[k].hint}
              className="px-2.5 py-1 text-[10px] font-bold rounded-md whitespace-nowrap"
              style={filter === k ? { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" } : { color: "#71717a" }}>
              {VERDICTS[k].label} {counts[k] ? `(${counts[k]})` : ""}
            </button>
          ))}
        </div>
      }
    >
      {!shown.length ? (
        <p className="text-[11px] text-zinc-400">No pages in this group.</p>
      ) : (
        <div className="divide-y divide-zinc-50">
          {shown.slice(0, 50).map(p => (
            <div key={p.url} className="py-3 grid grid-cols-1 md:grid-cols-[1.3fr_1.7fr_auto] gap-3 items-start">
              <div className="min-w-0">
                <a href={p.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-zinc-900 hover:underline inline-flex items-center gap-1 max-w-full">
                  <span className="truncate">{p.article_title || path(p.url)}</span> <ExternalLink size={10} className="flex-shrink-0 text-zinc-400" />
                </a>
                <p className="text-[10px] text-zinc-400 truncate">{path(p.url)}</p>
                <p className="text-[10px] text-zinc-500 mt-1 truncate" title={p.top_queries.join(", ")}>{p.top_queries.slice(0, 3).join(" · ")}</p>
              </div>
              <div className="text-[11px] leading-snug">
                <p className="text-zinc-700">{p.reason}</p>
                <p className="text-zinc-500 mt-0.5">→ {p.action}</p>
              </div>
              <div className="flex md:flex-col md:items-end gap-x-3 gap-y-0.5 text-[10px] text-zinc-500 whitespace-nowrap">
                <span>fit <b className="text-zinc-800">{fit2(p.fit)}</b> · pos <b className="text-zinc-800">{p.position ?? "—"}</b></span>
                <span>{fmt(p.impressions)} impr · {fmt(p.clicks)} clk</span>
                <span>{levelLabel(p.dominant_awareness, playbook)} · {ROLE_LABELS[p.dominant_role] || "—"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function AudiencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metric, setMetric] = usePersistentState("audience.metric", "impressions");
  const [building, setBuilding] = useState(false);
  const pollRef = useRef(null);
  const { hash } = useLocation();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setData(await api.getAudience()); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to load audience."); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  // Deep link from Search topics (#seg-<topic id>): scroll once the segment cards render.
  useEffect(() => {
    if (!data || !hash.startsWith("#seg-")) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data, hash]);

  const buildSegments = async () => {
    setBuilding(true);
    setError("");
    try { await api.buildAudienceSegments(); await load(); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Segment build failed."); }
    setBuilding(false);
  };

  // Shares the Topic Clusters history backfill: one GSC pull fills both page and query weeks.
  const startBackfill = async () => {
    try { await api.backfillClusterAuthority(); } catch (e) { setError(e?.message || "Backfill failed to start."); return; }
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const next = await api.getAudience().catch(() => null);
      if (!next) return;
      setData(next);
      if (next.trend.backfill?.status !== "running") clearInterval(pollRef.current);
    }, 3000);
    load();
  };

  const s = data?.summary;
  const empty = data && !s?.queries;

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Users size={16} strokeWidth={2} className="text-zinc-900" />
          <div>
            <h1 className="text-base font-bold text-zinc-900">Audience</h1>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Who your search traffic is, predicted from Search Console queries
              {data?.analyzed_at && <> · classified {new Date(data.analyzed_at).toLocaleDateString()}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
            {[["impressions", "Impressions"], ["clicks", "Clicks"]].map(([k, label]) => (
              <button key={k} onClick={() => setMetric(k)}
                className="px-2.5 py-1 text-[10px] font-bold rounded-md"
                style={metric === k ? { backgroundColor: PRIMARY, color: "#fff" } : { color: "#71717a" }}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading} title="Reload"
            className="p-1.5 text-zinc-500 hover:text-zinc-900 disabled:opacity-40">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
          <AlertTriangle size={13} className="flex-shrink-0" /> {error}
        </div>
      )}

      {loading && !data && <p className="text-xs text-zinc-400">Loading…</p>}

      {empty && (
        <div className="px-5 py-8 bg-white border border-zinc-100 rounded-xl text-center">
          <p className="text-sm font-bold text-zinc-800">No classified queries yet</p>
          <p className="text-[11px] text-zinc-500 mt-1">
            Audience is predicted from Gap Analysis. Run it from{" "}
            <Link to="/dashboard/search-performance?tab=queries" className="underline">Search Performance → Queries</Link>.
          </p>
        </div>
      )}

      {data && !empty && (
        <>
          {(s.missing_signals > 0 || s.fit_source === "commercial_value") && (
            <div className="flex items-start gap-2 px-4 py-2.5 mb-3 bg-amber-50 border border-amber-100 rounded-xl text-[11px] text-amber-800">
              <Info size={13} className="flex-shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                {s.missing_signals > 0 && (
                  <p>{fmt(s.missing_signals)} queries were classified before awareness/seniority existed —{" "}
                    <Link to="/dashboard/search-performance?tab=queries" className="underline">re-run Gap Analysis</Link> to fill them.</p>
                )}
                {s.fit_source === "commercial_value" && (
                  <p>"Your buyer" is estimated from commercial value.{" "}
                    <Link to="/dashboard/settings/business" className="underline">Add business context and personas</Link> for a real buyer-fit score.</p>
                )}
              </div>
            </div>
          )}

          <Headline data={data} />
          <div className="mb-4"><Pyramid data={data} metric={metric} /></div>
          <Who data={data} metric={metric} />

          {data.trend.weeks_stored < 8
            ? <BackfillCard state={data.trend.backfill} onStart={startBackfill} weeksStored={data.trend.weeks_stored} />
            : <TrendChart trend={data.trend} />}

          <Segments segments={data.segments} playbook={data.playbook} building={building} onBuild={buildSegments} />
          <UnmetDemand demand={data.unmet_demand} playbook={data.playbook} fromTopics={data.segments.source === "topics"} />
          <Pages pages={data.pages} playbook={data.playbook} />
        </>
      )}
    </div>
  );
}
